import { createFileRoute } from '@tanstack/react-router'
import { DocPage } from '../components/docs/DocPage'
import { Section, P, DefList, C } from '../components/docs/prose'

export const Route = createFileRoute('/docs/contribution-room' as any)({ component: ContributionRoom })

const SECTIONS = [
  { id: 'what', title: 'What it tracks' },
  { id: 'counting', title: 'What counts as a contribution' },
  { id: 'withdrawals', title: 'Why withdrawals are not netted' },
  { id: 'rrsp-window', title: 'The RRSP window' },
  { id: 'limits', title: 'What it does not do' },
]

function ContributionRoom() {
  return (
    <DocPage
      path="/docs/contribution-room"
      title="Contribution room"
      lede={
        <>
          TFSA, RRSP and FHSA room is one number per plan per person, however
          many accounts hold it. FolioWorth counts your ledger&apos;s actual
          deposits against the ceiling you set, so the answer to &ldquo;can I
          still contribute?&rdquo; comes from records, not memory.
        </>
      }
      sections={SECTIONS}
    >
      <Section id="what" title="What it tracks">
        <P>
          A limit is a name (&ldquo;TFSA 2026&rdquo;), a year, a dollar
          ceiling, a date window, and the set of accounts that share the room.
          The set matters: a TFSA at Wealthsimple and a TFSA at TD draw on the
          same room, so they belong in one limit. Two spouses&apos; RRSPs are
          separate room, so they get separate limits even at the same broker.
        </P>
        <P>
          The room-used bar reads from the ledger, not from a manually
          maintained tally. Record the deposit once, the way you already do,
          and the limit updates.
        </P>
      </Section>

      <Section id="counting" title="What counts as a contribution">
        <P>
          A deposit counts when money enters a linked account from a real
          account <em>outside</em> the limit&apos;s set. That single rule gets
          the three cases people worry about right by construction:
        </P>
        <DefList
          items={[
            ['Chequing → TFSA', 'Counted. New money crossed into the plan.'],
            [
              'TFSA #1 → TFSA #2, both in the limit',
              'Not counted. Moving room around is not using room.',
            ],
            [
              'Dividends or interest earned inside',
              'Not counted. Growth inside a registered plan never consumes room.',
            ],
          ]}
        />
        <P>
          Amounts are tracked in the account&apos;s own currency and in your
          base currency; the base-currency figure is what counts against the
          ceiling, matching how CRA counts CAD.
        </P>
      </Section>

      <Section id="withdrawals" title="Why withdrawals are not netted">
        <P>
          The page shows withdrawals separately and never subtracts them from
          room used. A TFSA withdrawal restores room on the following
          January&nbsp;1, not immediately &mdash; treating it as immediate is
          the classic way people trigger the 1% per month over-contribution
          tax. RRSP withdrawals never restore room at all.
        </P>
      </Section>

      <Section id="rrsp-window" title="The RRSP window">
        <P>
          RRSP deduction room runs sixty days past December&nbsp;31, so a
          deposit on February&nbsp;20 can belong to the prior year&apos;s
          limit. That is why a limit has an explicit start and end date rather
          than assuming the calendar year: set the window from
          March&nbsp;2 to March&nbsp;1 and first-sixty-days deposits land in
          the right year.
        </P>
      </Section>

      <Section id="limits" title="What it does not do">
        <P>
          FolioWorth does not know your actual CRA room &mdash; that depends
          on your income history, carry-forward, and past withdrawals filed
          with CRA. Read the number from your{' '}
          <C>CRA My Account</C> and enter it as the ceiling; FolioWorth then
          does the part CRA cannot: watch the deposits as they happen.
        </P>
      </Section>
    </DocPage>
  )
}
